import { AlarmClockPlus, Box, Replace, X } from "lucide-react";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGetSessionByKey } from "@/features/session/api/get-session-by-key";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { CallSettingsTab, CallSettingsTabType } from "../types/tab.types";

export function DrawerNavigation() {
  const { key } = useParams();
  const closeExtendSessionDrawer = useVidyoStore(
    (store) => store.moderationDrawer.close,
  );

  const setActiveTab = useVidyoStore(
    (store) => store.moderationDrawer.setActiveTab,
  );

  const { data: session, isSuccess: isSessionSuccess } = useGetSessionByKey(
    key || "",
  );

  const isSessionExpired = isSessionSuccess
    ? new Date(session.end) < new Date()
    : false;

  const handleTabClick = (tab: CallSettingsTabType) => {
    setActiveTab(tab);
  };

  const tabs: CallSettingsTab[] = [
    {
      label: "Session Settings",
      description: "Configure session settings",
      icon: Box,
      key: "session-settings",
    },
    {
      label: "Breakout Rooms",
      description: "Split into smaller group discussions",
      icon: Replace,
      key: "breakout-sessions",
      disabled: isSessionExpired,
    },
    {
      label: "Extend Session",
      description: "Increase the duration of this session",
      icon: AlarmClockPlus,
      key: "extend-session",
    },
  ];

  return (
    <div className="flex h-full flex-col">
      <p
        className="px-4 py-4 text-sm text-foreground"
        id="moderation-drawer-title"
      >
        Session Moderation
      </p>

      <Button
        variant="ghost"
        size="icon"
        onClick={closeExtendSessionDrawer}
        className="absolute right-2 top-2"
      >
        <X className="size-4" />
      </Button>

      <Separator className="mb-4 bg-border" />
      <TooltipProvider>
        <div className="flex flex-col px-4">
          <div className="flex flex-col gap-2">
            {tabs.map((tab) => {
              if (tab.disabled) {
                return (
                  <Tooltip delayDuration={0} key={tab.label}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-auto justify-start px-2"
                        disabled={tab.disabled}
                      >
                        <tab.icon className="mr-2 size-5 flex-shrink-0" />
                        <span className="flex flex-col items-start">
                          <span className="text-sm font-normal">
                            {tab.label}
                          </span>
                          <span className="text-xs font-normal text-muted">
                            {tab.description}
                          </span>
                        </span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        You cannot create a breakout room for a session that has
                        ended
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return (
                <Button
                  key={tab.label}
                  variant="ghost"
                  className="h-auto justify-start px-2"
                  onClick={() => handleTabClick(tab.key)}
                  disabled={tab.disabled}
                >
                  <tab.icon className="mr-2 size-5 flex-shrink-0" />
                  <span className="flex flex-col items-start">
                    <span className="text-sm font-normal">{tab.label}</span>
                    <span className="text-xs font-normal text-muted">
                      {tab.description}
                    </span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}
